import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  CalendarDays, FileDown, FileSignature, Inbox, Loader2, MapPin, MessageSquare, Phone, Search, Send, ShieldCheck, UserRound, UserX, Users,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useAuth } from '@/contexts/AuthContext';
import { useCustomersData, useSendCustomerReply } from '@/hooks/useCustomersAdmin';
import { useCustomerDataExport, useEraseCustomer, type CustomerIdentity } from '@/hooks/useDataExport';
import { customerMatchesSource } from '@/lib/customerProfiles';
import { useRecurringSeriesList, useRecurringTeamMembers } from '@/hooks/useRecurringSeries';
import { RepeatCleansList } from '@/components/admin/recurring/RepeatCleansList';
import { CustomerSmsOptOut } from '@/components/admin/CustomerSmsOptOut';
import {
  AdminPage,
  EmptyState,
  Panel,
  PersonAvatar,
  StatusChip,
  adminButton,
  adminTabs,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';

const MetaItem = ({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) => (
  <div className="flex min-w-0 items-start gap-2.5">
    <span className="adm-icon-tile h-8 w-8 rounded-[9px] [&_svg]:h-4 [&_svg]:w-4" aria-hidden="true">{icon}</span>
    <div className="min-w-0">
      <p className="text-[11.5px] font-medium text-muted-foreground">{label}</p>
      <p className="break-words text-[13px] font-medium text-foreground">{children}</p>
    </div>
  </div>
);

const CountBadge = ({ value }: { value: number }) => (
  <span className="adm-filter-count ml-0.5">{value}</span>
);

/** Small calendar tile ("THU / 29") like the dashboard job list. */
const DateTile = ({ date }: { date: Date }) => (
  <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl border border-border bg-muted/50 text-center leading-none">
    <span className="text-[10px] font-semibold uppercase text-muted-foreground">{format(date, 'MMM')}</span>
    <span className="adm-num mt-0.5 text-[15px] font-semibold text-foreground">{format(date, 'd')}</span>
  </div>
);

const CustomersAdmin = () => {
  useAdminPageTitle('Customers');
  const navigate = useNavigate();
  const { orgId, org, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { user, currentOrgRole, staffRole, isPlatformAdmin } = useAuth();
  const { toast } = useToast();
  const isOwner = !isPlatformAdmin && (currentOrgRole ? currentOrgRole === 'owner' : staffRole === 'owner');
  const customerExport = useCustomerDataExport();
  const eraseCustomer = useEraseCustomer();
  const [eraseOpen, setEraseOpen] = useState(false);

  const { data, isLoading: loading } = useCustomersData(orgId);
  const enquiries = data?.enquiries ?? [];
  const bookings = data?.bookings ?? [];
  const threads = data?.threads ?? [];
  const messages = data?.messages ?? [];

  const sendReplyMutation = useSendCustomerReply();
  const { data: allSeries = [] } = useRecurringSeriesList(orgId);
  const { data: seriesTeam = [] } = useRecurringTeamMembers(orgId);

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [reply, setReply] = useState('');

  // Derive stable customers reference from query data (avoids `?? []` new-array-per-render).
  const customers = useMemo(() => data?.customers ?? [], [data]);

  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId) ?? customers[0] ?? null;
  const customerThreads = threads.filter((thread) => customerMatchesSource(selectedCustomer, thread));
  const activeThread = customerThreads.find((thread) => thread.id === selectedThreadId) ?? customerThreads[0] ?? null;
  const activeMessages = messages.filter((message) => message.thread_id === activeThread?.id);
  const customerBookings = bookings.filter((booking) => customerMatchesSource(selectedCustomer, {
    customer_id: booking.customer_id,
    email: booking.client_email,
    phone: booking.client_phone,
    name: booking.client_name,
    address: booking.address,
  }));
  const customerSeries = allSeries.filter((series) => customerMatchesSource(selectedCustomer, {
    customer_id: series.customer_id,
    email: series.client_email,
    phone: series.client_phone,
    name: series.client_name,
    address: series.address,
  }));
  const customerEnquiries = enquiries.filter((enquiry) => customerMatchesSource(selectedCustomer, {
    customer_id: enquiry.customer_id,
    email: enquiry.client_email,
    phone: enquiry.client_phone,
    name: enquiry.client_name,
    address: enquiry.address,
  }));

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) =>
      [customer.name, customer.email, customer.phone, customer.address].some((value) =>
        value?.toLowerCase().includes(term)
      )
    );
  }, [customers, search]);

  const sendReply = async () => {
    if (!assertCanWrite()) return;
    if (!reply.trim() || !selectedCustomer || !activeThread || !orgId) return;
    try {
      await sendReplyMutation.mutateAsync({
        threadId: activeThread.id,
        orgId,
        customerId: selectedCustomer.id,
        senderId: user?.id,
        body: reply.trim(),
      });
      setReply('');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      toast({ title: 'Could not send reply', description: message, variant: 'destructive' });
    }
  };

  const identityOf = (customer: NonNullable<typeof selectedCustomer>): CustomerIdentity => ({
    customerId: customer.customer_id,
    email: customer.email || null,
    phone: customer.phone,
  });

  const exportCustomer = async () => {
    if (!selectedCustomer || !orgId) return;
    try {
      await customerExport.mutateAsync({
        orgId,
        orgName: org?.name,
        identity: identityOf(selectedCustomer),
        label: selectedCustomer.name || 'customer',
      });
      toast({ title: 'Customer data downloaded', description: 'Send the file to the customer if they asked for it.' });
    } catch (error: unknown) {
      toast({ title: 'Could not export customer data', description: (error as Error)?.message, variant: 'destructive' });
    }
  };

  const eraseSelected = async () => {
    if (!assertCanWrite()) return;
    if (!selectedCustomer || !orgId) return;
    try {
      await eraseCustomer.mutateAsync({ orgId, identity: identityOf(selectedCustomer) });
      setEraseOpen(false);
      setSelectedCustomerId(null);
      toast({ title: 'Customer erased', description: 'Their personal details have been removed. Invoice amounts are kept.' });
    } catch (error: unknown) {
      toast({ title: 'Could not erase customer', description: (error as Error)?.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminPage>
      <p className="-mb-1 text-[13.5px] text-muted-foreground">
        Customer profiles, booking history, enquiry history and messages.
      </p>

      <div className="grid items-start gap-4 lg:grid-cols-[320px_1fr]">
        <Panel
          title="Customer list"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          description={`${customers.length} customer${customers.length === 1 ? '' : 's'}`}
          padded={false}
          className="lg:sticky lg:top-24"
        >
          <div className="px-4 pb-3 pt-3 md:px-5">
            <Label htmlFor="customer-search" className="sr-only">Search</Label>
            <div className="adm-search">
              <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
              <input
                id="customer-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email, phone..."
              />
            </div>
          </div>
          <div className="adm-scroll max-h-[300px] space-y-0.5 overflow-y-auto border-t border-border p-2 lg:max-h-[calc(100vh-260px)]">
            {filteredCustomers.length === 0 ? (
              <EmptyState compact icon={<Search />} title="No customers found." />
            ) : (
              filteredCustomers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  data-active={selectedCustomer?.id === customer.id}
                  onClick={() => {
                    setSelectedCustomerId(customer.id);
                    setSelectedThreadId(null);
                  }}
                  className="adm-row items-start py-2.5"
                >
                  <PersonAvatar name={customer.name} seed={customer.id} size={34} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-semibold text-foreground">{customer.name}</p>
                    <p className="truncate text-[12px] text-muted-foreground">{customer.email}</p>
                    {customer.phone && (
                      <p className="adm-num truncate text-[11.5px] text-muted-foreground">{customer.phone}</p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </Panel>

        {selectedCustomer ? (
          <div className="min-w-0 space-y-4">
            <Panel>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3.5">
                  <PersonAvatar name={selectedCustomer.name} seed={selectedCustomer.id} size={52} />
                  <div className="min-w-0">
                    <h2 className="truncate text-[18px] font-semibold leading-tight text-foreground">
                      {selectedCustomer.name}
                    </h2>
                    <p className="truncate text-[13px] text-muted-foreground">{selectedCustomer.email}</p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={adminButton.outline}
                    disabled={!canWrite}
                    onClick={() => {
                      const params = new URLSearchParams({ name: selectedCustomer.name });
                      if (selectedCustomer.customer_id) params.set('customer', selectedCustomer.customer_id);
                      if (selectedCustomer.email) params.set('email', selectedCustomer.email);
                      if (selectedCustomer.phone) params.set('phone', selectedCustomer.phone);
                      if (selectedCustomer.address) params.set('address', selectedCustomer.address);
                      navigate(`/admin/quotes/new?${params.toString()}`);
                    }}
                  >
                    <FileSignature />
                    Create quote
                  </Button>
                  {[
                    { label: 'Bookings', value: customerBookings.length },
                    { label: 'Enquiries', value: customerEnquiries.length },
                    { label: 'Threads', value: customerThreads.length },
                  ].map((stat) => (
                    <div key={stat.label} className="min-w-[76px] rounded-xl border border-border bg-muted/40 px-3 py-2">
                      <p className="adm-num text-[17px] font-semibold leading-none text-foreground">{stat.value}</p>
                      <p className="mt-1 text-[11px] font-medium text-muted-foreground">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-5 grid gap-4 border-t border-border pt-4 sm:grid-cols-3">
                <MetaItem icon={<Phone />} label="Phone">{selectedCustomer.phone || '-'}</MetaItem>
                <MetaItem icon={<MapPin />} label="Address">{selectedCustomer.address || '-'}</MetaItem>
                <MetaItem icon={<CalendarDays />} label="Customer since">
                  {format(new Date(selectedCustomer.created_at), 'dd MMM yyyy')}
                </MetaItem>
              </div>
              {isOwner && <CustomerSmsOptOut customerId={selectedCustomer.customer_id} disabled={!canWrite} />}
              {isOwner && (
                <div
                  className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="customer-data-requests"
                >
                  <p className="flex items-start gap-2 text-[12.5px] text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    Data requests: give this customer a copy of their data, or erase it.
                  </p>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={adminButton.outline}
                      onClick={exportCustomer}
                      disabled={customerExport.isPending}
                    >
                      {customerExport.isPending ? <Loader2 className="animate-spin" /> : <FileDown />}
                      Export their data
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className={adminButton.danger}
                      onClick={() => setEraseOpen(true)}
                      disabled={!canWrite}
                    >
                      <UserX /> Erase customer…
                    </Button>
                  </div>
                </div>
              )}
            </Panel>

            <Tabs defaultValue="messages" className="space-y-4">
              <TabsList className={adminTabs.list}>
                <TabsTrigger value="messages" className={adminTabs.trigger}>
                  Messages
                </TabsTrigger>
                <TabsTrigger value="bookings" className={adminTabs.trigger}>
                  Bookings <CountBadge value={customerBookings.length} />
                </TabsTrigger>
                <TabsTrigger value="enquiries" className={adminTabs.trigger}>
                  Enquiries <CountBadge value={customerEnquiries.length} />
                </TabsTrigger>
                <TabsTrigger value="repeat-cleans" className={adminTabs.trigger}>
                  Repeat cleans <CountBadge value={customerSeries.length} />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="messages" className="mt-0">
                <Panel padded={false} className="overflow-hidden">
                  <div className="grid xl:grid-cols-[260px_1fr]">
                    <div className="border-b border-border xl:border-b-0 xl:border-r">
                      <div className="flex items-center gap-2 px-4 pb-2 pt-4">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-[13.5px] font-semibold text-foreground">Threads</h3>
                      </div>
                      <div className="space-y-0.5 p-2 pt-0">
                        {customerThreads.length === 0 ? (
                          <p className="px-2 py-3 text-[12.5px] text-muted-foreground">No message threads yet.</p>
                        ) : (
                          customerThreads.map((thread) => (
                            <button
                              key={thread.id}
                              type="button"
                              data-active={activeThread?.id === thread.id}
                              onClick={() => setSelectedThreadId(thread.id)}
                              className="adm-row block py-2"
                            >
                              <p className="truncate text-[13px] font-semibold text-foreground">
                                {thread.subject || 'Message thread'}
                              </p>
                              <p className="text-[11.5px] text-muted-foreground">
                                {thread.booking_id ? 'Booking' : 'Enquiry'} · {format(new Date(thread.created_at), 'dd MMM')}
                              </p>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-col">
                      <div className="border-b border-border px-4 py-3 md:px-5">
                        <h3 className="truncate text-[14px] font-semibold text-foreground">
                          {activeThread?.subject || 'Messages'}
                        </h3>
                        <p className="text-[12px] text-muted-foreground">
                          Conversation stays attached to this enquiry or booking.
                        </p>
                      </div>
                      <div className="adm-scroll max-h-[420px] min-h-56 flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4 md:px-5">
                        {activeMessages.length === 0 ? (
                          <p className="py-10 text-center text-[13px] text-muted-foreground">No messages in this thread.</p>
                        ) : (
                          activeMessages.map((message) => {
                            const mine = message.sender_type === 'admin';
                            return (
                              <div key={message.id} className={cn('flex items-end gap-2', mine && 'justify-end')}>
                                {!mine && (
                                  <PersonAvatar name={selectedCustomer.name} seed={selectedCustomer.id} size={26} />
                                )}
                                <div className="flex max-w-[80%] flex-col">
                                  <div
                                    className={cn(
                                      'rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed',
                                      mine
                                        ? 'rounded-br-md bg-primary text-primary-foreground'
                                        : 'rounded-bl-md border border-border bg-card text-foreground',
                                    )}
                                  >
                                    <p className="whitespace-pre-wrap break-words">{message.body}</p>
                                  </div>
                                  <p className={cn('mt-1 px-1 text-[11px] text-muted-foreground', mine && 'text-right')}>
                                    {format(new Date(message.created_at), 'dd MMM, HH:mm')}
                                  </p>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="space-y-2 border-t border-border p-3 md:px-5">
                        <Label htmlFor="admin-reply" className="sr-only">Reply</Label>
                        <div className="rounded-xl border border-input bg-card shadow-[var(--adm-shadow-xs)] focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/15">
                          <Textarea
                            id="admin-reply"
                            value={reply}
                            onChange={(event) => setReply(event.target.value)}
                            placeholder="Write a reply..."
                            disabled={!canWrite || !activeThread}
                            className="min-h-[64px] resize-none border-0 bg-transparent text-[13.5px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                          />
                          <div className="flex justify-end p-2 pt-0">
                            <Button
                              size="sm"
                              className={adminButton.primary}
                              onClick={sendReply}
                              disabled={!canWrite || !activeThread || sendReplyMutation.isPending || !reply.trim()}
                            >
                              {sendReplyMutation.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                              Send reply
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Panel>
              </TabsContent>

              <TabsContent value="bookings" className="mt-0">
                <Panel padded={false}>
                  {customerBookings.length === 0 ? (
                    <EmptyState icon={<CalendarDays />} title="No bookings yet." />
                  ) : (
                    <div className="adm-divide px-4 md:px-5">
                      {customerBookings.map((booking) => {
                        const when = new Date(`${booking.scheduled_date}T${booking.start_time}`);
                        return (
                          <div key={booking.id} className="flex items-center gap-3 py-3">
                            <DateTile date={when} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[13.5px] font-semibold text-foreground">{booking.title}</p>
                              <p className="adm-num text-[12px] text-muted-foreground">{format(when, 'EEE dd MMM yyyy, HH:mm')}</p>
                            </div>
                            <StatusChip status={booking.status} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="enquiries" className="mt-0">
                <Panel padded={false}>
                  {customerEnquiries.length === 0 ? (
                    <EmptyState icon={<Inbox />} title="No enquiries yet." />
                  ) : (
                    <div className="adm-divide px-4 md:px-5">
                      {customerEnquiries.map((enquiry) => (
                        <div key={enquiry.id} className="flex items-center gap-3 py-3">
                          <span className="adm-icon-tile adm-icon-tile--violet" aria-hidden="true">
                            <Inbox />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-semibold text-foreground">{enquiry.subject || 'Enquiry'}</p>
                            <p className="text-[12px] text-muted-foreground">{format(new Date(enquiry.created_at), 'dd MMM yyyy')}</p>
                          </div>
                          <StatusChip status={enquiry.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              </TabsContent>

              <TabsContent value="repeat-cleans" className="mt-0">
                <Panel padded={false}>
                  <RepeatCleansList
                    rows={customerSeries}
                    teamMembers={seriesTeam}
                    canWrite={canWrite}
                    hideClient
                    emptyTitle="No repeat cleans for this customer."
                    emptyDescription="Open one of their jobs in Jobs & Calendar and choose “Make this a repeat clean”."
                  />
                </Panel>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <Panel>
            <EmptyState
              icon={<UserRound />}
              title="Select a customer to view their account."
              description="Profiles are built from bookings, enquiries and saved customer records."
            />
          </Panel>
        )}
      </div>
      <AlertDialog open={eraseOpen} onOpenChange={(open) => !eraseCustomer.isPending && setEraseOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Erase {selectedCustomer?.name ?? 'this customer'}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>This can’t be undone. Export their data first if they asked for a copy.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Their customer record, enquiries, callback requests and messages are deleted.</li>
                  <li>Their bookings stay on your calendar, but without their name, contact details, address or notes.</li>
                  <li>Invoices are kept for your accounts, with the name changed to “Erased customer”.</li>
                  <li>Reviews aren’t linked to customers, so remove any in Reviews yourself.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eraseCustomer.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={eraseCustomer.isPending}
              onClick={(event) => {
                event.preventDefault();
                void eraseSelected();
              }}
            >
              {eraseCustomer.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Erase customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default CustomersAdmin;
